import { Link } from "react-router-dom";
import { useState } from "react";
import Layout from "@/components/Layout";
import Reveal from "@/components/Reveal";
import CopyBlock from "@/components/CopyBlock";
import { MEDIA_FOLDER_URL } from "@/lib/press";

/**
 * /media-kit
 *
 * Editorial press hub — quick reference table, three bio lengths,
 * curator bios, verified quotes, "why this matters today" pillars,
 * sources, and downloads for the typeset PDFs.
 *
 * Every bio + quote block has a one-click Copy button so journalists
 * can lift the exact wording without re-formatting.
 */

const PDF_FACT_SHEET = `${process.env.REACT_APP_BACKEND_URL}/api/press/fact-sheet.pdf`;
const PDF_RELEASE = `${process.env.REACT_APP_BACKEND_URL}/api/press/release.pdf`;

const QUICK_REFERENCE = [
  ["Name", "Nick Fraser"],
  ["Dates", "1946 — 2023"],
  ["Origin", "London's East End; connected to Blackheath Common in the family story."],
  [
    "Working life",
    "Over three decades at Tate & Lyle, alongside a life shaped by care, routine and private creativity.",
  ],
  [
    "Artistic identity",
    "Self-taught / outsider artist; often described as “the East End's Van Gogh”.",
  ],
  [
    "Materials",
    "Household paint, construction and decorating materials, cardboard, wood, discarded boards and other accessible surfaces.",
  ],
  [
    "Forms",
    "Large-scale works and modern polyptychs, often built across multiple panels.",
  ],
  [
    "Themes",
    "Spirituality, cosmic symbolism, religious tension, masculinity, identity, consciousness, solitude, memory and emotional expression.",
  ],
  [
    "First major recent chapter",
    "Nick Fraser: Chapter One, The Crypt Gallery, London — 2024.",
  ],
  [
    "Next major event",
    "Nick Fraser: Chapter 2, Shoreditch Arts Club — 31 July 2026.",
  ],
  [
    "Estate / curatorial context",
    "Preserved and shared through Mark O'Shaughnessy and Marcus O'Shaughnessy, with Imaginative Reaction supporting the wider mission.",
  ],
];

const NICK_BIO_SHORT =
  "Nick Fraser (1946–2023) was a self-taught outsider artist from London's East End. A former Tate & Lyle factory worker and lifelong carer to his mother, Fraser created a vast private body of work using household, construction and decorating materials. His large-scale, symbolic paintings explore identity, masculinity, spirituality, consciousness and solitude. After decades hidden from public view, his work is now being preserved, curated and introduced to new audiences.";

const NICK_BIO_MEDIUM =
  "Nick Fraser (1946–2023) was a visionary self-taught artist from London's East End whose extraordinary body of work remained largely unseen during his lifetime. Born into a traditional working-class world and shaped by decades at the Tate & Lyle factory, Fraser lived quietly, cared for his mother, and painted in private. Without formal art training, he used whatever materials were close at hand — household paints, construction remnants, boards, cardboard and decorating tools — transforming them into large-scale, often multi-panel works. His paintings move between sacred imagery, cosmic symbolism, raw abstraction and the human figure, opening a deeply personal world of identity, masculinity, longing, spiritual tension and emotional survival. Following his death in 2023, Fraser's archive has been brought into public view through the care of Mark O'Shaughnessy and Marcus O'Shaughnessy, revealing a powerful, hidden voice in British outsider art.";

const NICK_BIO_LONG_P1 =
  "Nick Fraser (1946–2023) was a self-taught British outsider artist whose private, expansive and emotionally charged body of work is now being recognised after decades out of public view. Born into East London's working-class culture, Fraser spent more than thirty years working at Tate & Lyle while living a life defined by duty, routine, care and solitude. Behind that public identity, he sustained a private creative world of remarkable scale and intensity.";
const NICK_BIO_LONG_P2 =
  "Fraser taught himself to paint without art-school instruction, using the practical materials available to him: household paint, construction scraps, decorating tools, cardboard, wood and found boards. Many works are large-scale and multi-panel, forming fierce, modern polyptychs. They carry echoes of sacred art, religious anxiety, cosmic symbolism and raw abstraction, while repeatedly returning to the body, identity, masculinity, consciousness and the unspoken interior life.";
const NICK_BIO_LONG_P3 =
  "His story is also a story of preservation. Mark O'Shaughnessy, Nick's lifelong friend, documentarian and advocate, recorded rare archive footage and protected the memory of the man behind the paintings. Today, Mark and his son Marcus O'Shaughnessy are bringing Fraser's work into the light through exhibitions, film, research, prints and public storytelling. Nick Fraser: Chapter 2 continues that journey, inviting new audiences to encounter a hidden East End painter whose work speaks urgently to vulnerability, private truth and the need to recognise artists who lived beyond the structures of the art world.";

const MARCUS_BIO_SHORT =
  "Marcus O'Shaughnessy is a curator, creative producer, youth engagement specialist and founder of Imaginative Reaction CIC. With over a decade of experience across alternative education, arts practice and mental health support for young people, he creates inclusive platforms for emerging, outsider and underrepresented artists. As co-custodian and curator of the Nick Fraser legacy, Marcus curated Nick Fraser: Chapter One at The Crypt Gallery and continues to develop the Chapter 2 exhibition journey.";

const MARCUS_BIO_LONG_P1 =
  "Marcus O'Shaughnessy is a curator, creative producer, digital storyteller and founder of Imaginative Reaction CIC, a creative platform built to support emerging, outsider and underrepresented artists through curation, collaboration and community. His work draws on more than a decade in youth engagement, alternative education and mental health support, alongside a parallel creative life as a music producer, DJ and multidisciplinary practitioner.";
const MARCUS_BIO_LONG_P2 =
  "In 2023, Marcus stepped into a deeper curatorial role following the death of Nick Fraser, a visionary East End artist and close family friend whose vast body of work had remained largely unseen. Working alongside his father Mark O'Shaughnessy — Nick's lifelong friend, muse and documentarian — Marcus became a key custodian of the collection. He curated the debut public exhibition Nick Fraser: Chapter One at The Crypt Gallery, London, where artwork, archive film, live performance and storytelling combined to introduce audiences to the man behind the paintings.";
const MARCUS_BIO_LONG_P3 =
  "Marcus's curatorial approach is rooted in emotional truth, access and legacy. Rather than presenting Nick Fraser as a discovery detached from life, he frames the work through friendship, class, care, memory and the right of unheard artists to be seen. Through Imaginative Reaction and the Nick Fraser project, he is building a bridge between private creative worlds and public recognition, while creating space for new artists who might otherwise remain overlooked.";

const MARCUS_ONE_LINE =
  "Marcus O'Shaughnessy — Curator, creative producer, founder of Imaginative Reaction CIC and co-custodian of the Nick Fraser legacy.";

const QUOTES = [
  {
    body: "“Nothing will stop me from painting.”",
    meta: "Attributed to Nick Fraser · Chapter 1 film context — reported in Strand Magazine.",
    id: "q-nick-1",
  },
  {
    body: "“An installation — somewhere, wherever.”",
    meta: "Attributed to Nick Fraser · Adapted from Strand Magazine's description.",
    id: "q-nick-2",
  },
  {
    body: "“An artist to remember, and a curator to watch.”",
    meta: "Sophie-May Ward-Marchbank · Strand Magazine, reviewing Chapter 1.",
    id: "q-strand-1",
  },
  {
    body: "“Unforgettable, first shown again here after 30 years.”",
    meta: "Strand Magazine · on the public re-emergence of the works.",
    id: "q-strand-2",
  },
  {
    body: "“Spiritual, tribal, ethereal — a faintly ominous other-worldly experience.”",
    meta: "Visitor testimonial · nickfraserartist.com.",
    id: "q-visitor",
  },
  {
    body: "“Bringing Nick's work to light, sharing his story and supporting the wider community.”",
    meta: "Marcus O'Shaughnessy · Vibe 107.6 FM.",
    id: "q-marcus-vibe",
  },
  {
    body: "“Nick Fraser painted the things many men of his generation were never given the language to say.”",
    meta: "Original press-pack line · curatorial positioning.",
    id: "q-pack-1",
  },
  {
    body: "“His work turns private silence into public testimony.”",
    meta: "Original press-pack line · for captions, wall text and outreach.",
    id: "q-pack-2",
  },
];

const PILLARS = [
  {
    title: "A hidden voice in British outsider art",
    body:
      "Fraser created outside the usual pathways of art school, galleries and institutional validation. His story helps widen the definition of who gets to be called an artist and which lives are allowed into cultural history.",
  },
  {
    title: "A working-class archive of imagination",
    body:
      "His life at Tate & Lyle and his use of ordinary materials root the work in a practical East End world. The paintings transform everyday surfaces into spiritual and psychological spaces.",
  },
  {
    title: "A language for male vulnerability",
    body:
      "Fraser's paintings return to bodies, longing, identity and private contradiction. Today, they offer a moving way to talk about generations of men who were often taught endurance but not emotional expression.",
  },
  {
    title: "Spirituality without certainty",
    body:
      "The work draws on religious forms, cosmic symbols, abstract figures and archetypal scenes. It feels devotional, unsettled and searching rather than neatly resolved.",
  },
  {
    title: "A legacy of friendship and care",
    body:
      "The survival of the archive is inseparable from Mark O'Shaughnessy's friendship and Marcus O'Shaughnessy's curatorial work. The project shows how legacy can be protected by love, memory and community before institutions arrive.",
  },
  {
    title: "A story for contemporary audiences",
    body:
      "In a time when mental health, identity and belonging are central public conversations, Fraser's work feels newly legible. It asks viewers to look again at silence, solitude and the private labour of becoming oneself.",
  },
];

const SOURCES = [
  ["Nick Fraser Art official website", "nickfraserartist.com", "https://www.nickfraserartist.com"],
  ["Imaginative Reaction — Nick Fraser profile", "imaginative-reaction.org/nickfraser", "https://imaginative-reaction.org/nickfraser"],
  ["Strand Magazine review by Sophie-May Ward-Marchbank", "strandmagazine.co.uk", "https://www.strandmagazine.co.uk/single-post/nick-fraser-at-the-crypt-gallery-resurrecting-east-london-s-van-gogh"],
  ["Imaginative Reaction — Marcus O'Shaughnessy profile", "imaginative-reaction.org/marxjames", "https://imaginative-reaction.org/marxjames"],
  ["Vibe 107.6 FM local news feature", "vibe1076.co.uk", "https://www.vibe1076.co.uk"],
  ["MutualArt artist listing", "mutualart.com", "https://www.mutualart.com"],
  ["Balmly Substack — guest essay by Marcus O'Shaughnessy", "balmly.substack.com", "https://balmly.substack.com"],
];

const EMAIL_BODY_PLAIN = `Dear [Name],

I'd like to invite you to Nick Fraser: Chapter 2 — a one-day exhibition from the estate of the late East End outsider artist Nick Fraser (1946–2023), opening at Shoreditch Arts Club on 31 July 2026.

Following the success of Chapter 1 at The Crypt Gallery, this next chapter brings together original works, immersive projection, rare archive film, a cinema screening and the first public release of limited-edition Nick Fraser prints. The evening private view (7pm) includes a live performance from BoThai.

Fraser worked at the Tate & Lyle factory for over thirty years while privately producing a large body of paintings exploring masculinity, spirituality, memory and identity. His archive is now being carefully released by his lifelong friend Mark O'Shaughnessy and curator Marcus O'Shaughnessy.

Full press release, media fact sheet, biographies, quotes and a Google Drive press image library are available at chapter2.nickfraserartist.com/press-release. I'd be delighted to arrange an interview with Marcus, a private walkthrough, or accreditation for the day or evening — just reply to this email.

With thanks,
[Your name]
info@nickfraserartist.com · chapter2.nickfraserartist.com`;

export default function MediaKit() {
  return (
    <Layout>
      <main
        data-testid="media-kit-page"
        className="bg-[color:var(--bone)] text-[color:var(--ink)]"
      >
        {/* ── Masthead ─────────────────────────────────────────────── */}
        <section
          className="bone-section pt-40 md:pt-56 pb-16 md:pb-24 border-b border-[color:var(--rule)]"
          data-testid="mk-masthead"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">Media Fact Sheet</p>
              <h1
                className="font-serif italic text-5xl md:text-7xl leading-[0.98] tracking-tight"
                data-testid="mk-title"
              >
                Nick Fraser
              </h1>
              <p className="font-serif italic text-xl md:text-2xl mt-4 opacity-75">
                1946 — 2023
              </p>
              <p className="font-serif italic text-xl md:text-2xl mt-10 opacity-85 max-w-3xl leading-[1.4]">
                Artist biography  ·  curator biography  ·  verified quotes  ·
                key context. Everything press, venues, galleries and editors
                need to write about Nick Fraser and Chapter 2.
              </p>

              <div className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href={PDF_FACT_SHEET}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mk-download-fact-sheet"
                  className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
                >
                  Download fact sheet (PDF)
                </a>
                <a
                  href={MEDIA_FOLDER_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mk-media-folder"
                  className="hairline border border-[color:var(--ink)] px-6 py-3 hover:bg-[color:var(--ink)] hover:text-[color:var(--bone)] transition-colors duration-500"
                >
                  Press image library →
                </a>
                <a
                  href={PDF_RELEASE}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mk-download-release"
                  className="hairline opacity-80 hover:opacity-100 transition-opacity"
                >
                  Download press release (PDF)
                </a>
                <Link
                  to="/press-release"
                  className="hairline opacity-80 hover:opacity-100 transition-opacity"
                >
                  View press release →
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Quick reference ──────────────────────────────────────── */}
        <section
          className="bone-section py-20 md:py-32"
          data-testid="mk-quick-reference"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">Quick reference</p>
              <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.05]">
                The fact sheet, at a glance.
              </h2>
            </Reveal>

            <Reveal slow>
              <dl className="mt-14 border-t border-[color:var(--rule)]">
                {QUICK_REFERENCE.map(([k, v]) => (
                  <div
                    key={k}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-8 py-6 md:py-7 border-b border-[color:var(--rule)]"
                  >
                    <dt className="md:col-span-4 hairline opacity-55">{k}</dt>
                    <dd className="md:col-span-8 font-serif italic text-lg md:text-xl leading-snug">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
        </section>

        {/* ── Nick bios ────────────────────────────────────────────── */}
        <section
          className="bone-section py-20 md:py-32 border-t border-[color:var(--rule)]"
          data-testid="mk-nick-bios"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">Nick Fraser — biography</p>
              <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.05]">
                Three bio lengths, ready to paste.
              </h2>
            </Reveal>

            <div className="mt-16 space-y-16">
              <CopyBlock
                eyebrow="Short bio · 75 words"
                body={NICK_BIO_SHORT}
                testid="mk-nick-bio-short"
              />
              <CopyBlock
                eyebrow="Medium bio · 150 words"
                body={NICK_BIO_MEDIUM}
                testid="mk-nick-bio-medium"
              />

              {/* Extended — 3 paragraphs, single copy */}
              <div
                data-testid="mk-nick-bio-extended"
                className="relative group border-t border-[color:var(--rule)] pt-8 md:pt-10"
              >
                <div className="flex items-baseline justify-between gap-6 mb-4">
                  <p className="hairline opacity-60">
                    Extended bio · press / programme
                  </p>
                  <ExtendedCopyButton
                    text={`${NICK_BIO_LONG_P1}\n\n${NICK_BIO_LONG_P2}\n\n${NICK_BIO_LONG_P3}`}
                    testid="mk-nick-bio-extended-copy"
                  />
                </div>
                <div className="space-y-5 font-serif italic text-lg md:text-xl leading-[1.55] opacity-90">
                  <p>{NICK_BIO_LONG_P1}</p>
                  <p>{NICK_BIO_LONG_P2}</p>
                  <p>{NICK_BIO_LONG_P3}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Marcus curator bio ───────────────────────────────────── */}
        <section
          className="bone-section py-20 md:py-32 border-t border-[color:var(--rule)]"
          data-testid="mk-curator-bios"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">
                Marcus O'Shaughnessy — curator
              </p>
              <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.05]">
                Curator, creative producer and founder.
              </h2>
            </Reveal>

            <div className="mt-16 space-y-16">
              <CopyBlock
                eyebrow="Short bio · 75 words"
                body={MARCUS_BIO_SHORT}
                testid="mk-marcus-bio-short"
              />

              <div
                data-testid="mk-marcus-bio-extended"
                className="relative group border-t border-[color:var(--rule)] pt-8 md:pt-10"
              >
                <div className="flex items-baseline justify-between gap-6 mb-4">
                  <p className="hairline opacity-60">
                    Extended bio · press / programme
                  </p>
                  <ExtendedCopyButton
                    text={`${MARCUS_BIO_LONG_P1}\n\n${MARCUS_BIO_LONG_P2}\n\n${MARCUS_BIO_LONG_P3}`}
                    testid="mk-marcus-bio-extended-copy"
                  />
                </div>
                <div className="space-y-5 font-serif italic text-lg md:text-xl leading-[1.55] opacity-90">
                  <p>{MARCUS_BIO_LONG_P1}</p>
                  <p>{MARCUS_BIO_LONG_P2}</p>
                  <p>{MARCUS_BIO_LONG_P3}</p>
                </div>
              </div>

              <CopyBlock
                eyebrow="Useful one-line credit"
                body={MARCUS_ONE_LINE}
                testid="mk-marcus-one-line"
              />
            </div>
          </div>
        </section>

        {/* ── Quotes ───────────────────────────────────────────────── */}
        <section
          className="dark-section py-24 md:py-40"
          data-testid="mk-quotes"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">Verified, usable lines</p>
              <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.05] max-w-[28ch]">
                For pull-quotes, captions, wall text and outreach.
              </h2>
              <p className="font-serif italic text-base md:text-lg opacity-70 mt-6 max-w-2xl leading-relaxed">
                For exact verbatim use of Nick's spoken words from archive
                footage, cross-check against the Chapter 1 film transcript
                before publication.
              </p>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
              {QUOTES.map((q) => (
                <CopyBlock
                  key={q.id}
                  body={q.body}
                  meta={q.meta}
                  testid={`mk-quote-${q.id}`}
                  textForClipboard={q.body}
                  dark
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── Why Nick Fraser matters today ───────────────────────── */}
        <section
          className="bone-section py-24 md:py-40"
          data-testid="mk-pillars"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">
                Context &amp; cultural relevance
              </p>
              <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.05] max-w-[24ch]">
                Why Nick Fraser matters today.
              </h2>
              <p className="font-serif italic text-lg md:text-xl opacity-80 mt-8 max-w-3xl leading-[1.55]">
                Fraser's work sits at the intersection of art, class, silence,
                masculinity, spirituality and emotional survival. His paintings
                were not made for the market first — they were made from
                necessity, from a private need to process questions that did
                not have an easy public language.
              </p>
            </Reveal>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
              {PILLARS.map((p, i) => (
                <Reveal key={p.title} delay={i * 60} slow>
                  <div className="border-t border-[color:var(--rule)] pt-8 md:pt-10">
                    <h3 className="font-serif italic text-2xl md:text-3xl leading-[1.15]">
                      {p.title}
                    </h3>
                    <p className="font-serif italic text-base md:text-lg leading-[1.55] opacity-85 mt-5 max-w-prose">
                      {p.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal slow>
              <div className="mt-20 md:mt-24">
                <CopyBlock
                  eyebrow="Suggested press angle"
                  body="“The East End artist who painted in private for decades — and whose hidden world is now speaking directly to our conversations about masculinity, memory, identity and outsider art.”"
                  testid="mk-press-angle"
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Outreach email template ──────────────────────────────── */}
        <section
          className="bone-section py-20 md:py-32 border-t border-[color:var(--rule)]"
          data-testid="mk-email-template"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="hairline opacity-60 mb-6">Outreach</p>
              <h2 className="font-serif italic text-3xl md:text-5xl leading-[1.05]">
                A ready-to-send press email.
              </h2>
              <p className="font-serif italic text-lg md:text-xl opacity-80 mt-6 max-w-2xl leading-relaxed">
                Copy and adapt the template below when reaching out to
                journalists, editors and listings desks. Subject line + body
                are ready to go.
              </p>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              <CopyBlock
                eyebrow="Subject line"
                body="Press invite — Nick Fraser: Chapter 2, Shoreditch Arts Club, 31 July"
                testid="mk-email-subject"
              />
              <CopyBlock
                eyebrow="Short pitch · 1 sentence"
                body="A one-day exhibition of paintings, archive film and a limited edition print release from the estate of the late East End outsider artist Nick Fraser — 31 July 2026 at Shoreditch Arts Club."
                testid="mk-email-pitch"
              />
            </div>

            <Reveal slow>
              <div
                data-testid="mk-email-body"
                className="relative group border-t border-[color:var(--rule)] mt-10 pt-8 md:pt-10"
              >
                <div className="flex items-baseline justify-between gap-6 mb-4">
                  <p className="hairline opacity-60">Email body — full template</p>
                  <ExtendedCopyButton
                    text={EMAIL_BODY_PLAIN}
                    testid="mk-email-body-copy"
                  />
                </div>
                <div className="space-y-5 font-serif italic text-lg md:text-xl leading-[1.55] opacity-90 max-w-[68ch]">
                  <p>Dear [Name],</p>
                  <p>
                    I'd like to invite you to <strong className="not-italic font-serif italic">Nick Fraser: Chapter 2</strong> — a one-day exhibition from the estate of the late East End outsider artist Nick Fraser (1946–2023), opening at Shoreditch Arts Club on 31 July 2026.
                  </p>
                  <p>
                    Following the success of Chapter 1 at The Crypt Gallery, this next chapter brings together original works, immersive projection, rare archive film, a cinema screening and the first public release of limited-edition Nick Fraser prints. The evening private view (7pm) includes a live performance from BoThai.
                  </p>
                  <p>
                    Fraser worked at the Tate &amp; Lyle factory for over thirty years while privately producing a large body of paintings exploring masculinity, spirituality, memory and identity. His archive is now being carefully released by his lifelong friend Mark O'Shaughnessy and curator Marcus O'Shaughnessy.
                  </p>
                  <p>
                    Full press release, media fact sheet, biographies, quotes and a Google Drive press image library are available at chapter2.nickfraserartist.com/press-release. I'd be delighted to arrange an interview with Marcus, a private walkthrough, or accreditation for the day or evening — just reply to this email.
                  </p>
                  <p>
                    With thanks,<br />
                    [Your name]<br />
                    info@nickfraserartist.com · chapter2.nickfraserartist.com
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Contact & sources ───────────────────────────────────── */}
        <section
          className="dark-section py-28 md:py-40"
          data-testid="mk-contact"
        >
          <div className="max-w-[1100px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-5">
              <p className="hairline opacity-60 mb-6">Press contact</p>
              <h2 className="font-serif italic text-3xl md:text-4xl leading-[1.1]">
                Interviews, images, accreditation.
              </h2>
              <div className="mt-10 space-y-5">
                <div>
                  <p className="hairline opacity-55">Email</p>
                  <p className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href="mailto:info@nickfraserartist.com"
                      className="link-quiet"
                      data-testid="mk-email"
                    >
                      info@nickfraserartist.com
                    </a>
                  </p>
                </div>
                <div>
                  <p className="hairline opacity-55">Web</p>
                  <p className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href="https://www.nickfraserartist.com"
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet"
                    >
                      www.nickfraserartist.com
                    </a>
                  </p>
                </div>
                <div>
                  <p className="hairline opacity-55">Instagram</p>
                  <p className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href="https://instagram.com/nickfraserart"
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet"
                    >
                      @nickfraserart
                    </a>
                    <span className="opacity-50">  ·  </span>
                    <a
                      href="https://instagram.com/Marxjamesart"
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet"
                    >
                      @Marxjamesart
                    </a>
                  </p>
                </div>
                <div>
                  <p className="hairline opacity-55">Project platform</p>
                  <p className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href="https://www.imaginative-reaction.org"
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet"
                    >
                      www.imaginative-reaction.org
                    </a>
                  </p>
                </div>
                <div>
                  <p className="hairline opacity-55">Press image library</p>
                  <p className="font-serif italic text-xl md:text-2xl mt-1">
                    <a
                      href={MEDIA_FOLDER_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="link-quiet"
                      data-testid="mk-media-folder-contact"
                    >
                      Photography, artwork &amp; flyers →
                    </a>
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal className="md:col-span-7" slow>
              <p className="hairline opacity-60 mb-6">References &amp; sources</p>
              <p className="font-serif italic text-base md:text-lg opacity-75 mb-8 leading-relaxed max-w-prose">
                This fact sheet is compiled from public-facing project pages,
                press / review coverage and Marcus O'Shaughnessy's published
                writing. Some language has been edited into media-pack form
                for clarity.
              </p>
              <ol
                className="border-t border-white/15"
                data-testid="mk-sources"
              >
                {SOURCES.map(([name, host, url], i) => (
                  <li
                    key={url}
                    className="border-b border-white/15 py-5 md:py-6 flex items-baseline gap-6"
                  >
                    <span className="hairline opacity-50 w-10 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-serif italic text-lg md:text-xl">
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="link-quiet"
                        >
                          {name}
                        </a>
                      </p>
                      <p className="hairline opacity-55 mt-2">{host}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
                <a
                  href={PDF_FACT_SHEET}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mk-download-fact-sheet-bottom"
                  className="hairline border border-[color:var(--bone)] px-6 py-3 hover:bg-[color:var(--bone)] hover:text-[color:var(--ink)] transition-colors duration-500"
                >
                  Download fact sheet (PDF)
                </a>
                <a
                  href={MEDIA_FOLDER_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mk-media-folder-bottom"
                  className="link-quiet font-serif italic text-lg"
                >
                  Press image library →
                </a>
                <Link
                  to="/press"
                  className="link-quiet font-serif italic text-lg"
                  data-testid="mk-back-to-press"
                >
                  Back to press →
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
    </Layout>
  );
}

// Inline button used by extended bio blocks (multi-paragraph copy).
function ExtendedCopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* no-op */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      className="hairline opacity-70 hover:opacity-100 transition-opacity shrink-0"
    >
      {copied ? "Copied ✓" : "Copy"}
    </button>
  );
}
